<template>
  <div class="content">
    <div class="container-fluid">
      <h3>New Relic</h3>

      <div class="row my-row" v-if="loading">
        Loading...
      </div>
      <div class="row" v-if="!loading">
          <div class="col-12">
            <ul class="nav nav-tabs">
              <li class="nav-item">
                <span class="nav-link" v-bind:class="{active: show_usage}" @click="showUsage">Usage</span>
              </li>
              <li class="nav-item">
                <span class="nav-link" v-bind:class="{active: show_users}" @click="showUsers">Users</span>
              </li>
            </ul>
          </div>
      </div>
      <div class="row" v-if="!loading && show_users">
        <div class="col-md-10">
          <strong>Users per account</strong>
          <DxSankey
            id="users_sankey"
            :data-source="users_sankey"
            source-field="source"
            target-field="target"
            weight-field="weight"
          >
            <DxSize :height="2000" />
            <DxTooltip
              :enabled="true"
              :customize-link-tooltip="customizeLinkTooltip"
            />
            <DxNode
              :width="10"
              :padding="20"
            />
            <DxLink color-mode="gradient"/>
          </DxSankey>
        </div>
        <div class="col-md-10">
          <strong>Users by Tier</strong>
          <DxSankey
            id="tier_sankey"
            :data-source="tier_sankey"
            source-field="source"
            target-field="target"
            weight-field="weight"
          >
            <DxSize :height="2000" />
            <DxTooltip
              :enabled="true"
              :customize-link-tooltip="customizeLinkTooltip"
            />
            <DxNode
              :width="10"
              :padding="20"
            />
            <DxLink color-mode="gradient"/>
          </DxSankey>
        </div>
      </div>
      <div class="row" v-if="!loading && show_usage" v-for="(sankey, date) in sankeys">
        <div class="col-md-10">
          <strong>GB ingested in {{date}}</strong>
          <DxSankey
            id="sankey"
            :data-source="sankey"
            source-field="source"
            target-field="target"
            weight-field="weight"
          >
            <DxSize :height="800" />
            <DxTooltip
              :enabled="true"
              :customize-link-tooltip="customizeLinkTooltip"
            />
            <DxNode
              :width="10"
              :padding="15"
            />
            <DxLink color-mode="gradient"/>
          </DxSankey>
        </div>
      </div>
      <div class="row" v-if="!loading && show_usage" v-for="(cost_data, index) in nr_costs_data">
        <div class="col-12 cost">
          <chart-card :key='cost_data' :chart-data="cost_data.data"
                      :chart-options="cost_data.options"
                      :responsive-options="cost_data.responsiveOptions"
                      chart-type="Bar"
          >
            <template slot="header">
              <h4 class="card-title">Costs Breakdown for {{index}} last month</h4>
              <p class="card-category">Values in $</p>
            </template>
          </chart-card>
        </div>
      </div>
      <div class="row" v-if="!loading && show_usage">
        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_apm_hosts' :chart-data="nr_apm_hosts_data.data"
                      :chart-options="chart_base.options"
                      :responsive-options="chart_base.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">APM Hosts</h4>
              <p class="card-category">Theorectical Cost in $</p>
            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>
        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_mobile' :chart-data="nr_mobile_data.data"
                      :chart-options="nr_mobile_data.options"
                      :responsive-options="nr_mobile_data.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">Mobile Usage</h4>
              <p class="card-category">Theorectical Cost in $</p>
            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>

        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_infra_cu' :chart-data="nr_infra_cu_data.data"
                      :chart-options="nr_infra_cu_data.options"
                      :responsive-options="nr_infra_cu_data.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">Infrastructure C.U.</h4>
              <p class="card-category">Theorectical Cost in $</p>

            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>

        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_insights' :chart-data="nr_insights_data.data"
                      :chart-options="nr_insights_data.options"
                      :responsive-options="nr_insights_data.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">Insights Paid Events</h4>
              <p class="card-category">Theorectical Cost in $</p>

            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>

        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_browser_pv' :chart-data="nr_browser_pv_data.data"
                      :chart-options="nr_browser_pv_data.options"
                      :responsive-options="nr_browser_pv_data.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">Browser Page Views</h4>
              <p class="card-category">Theorectical Cost in $</p>

            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>

        <div class="col-12 my-col col-xl-6 col-lg-12 col-sm-12 rotated-labels" >
          <chart-card :key='nr_synthetic' :chart-data="nr_synthetic_data.data"
                      :chart-options="nr_synthetic_data.options"
                      :responsive-options="nr_synthetic_data.responsiveOptions"
                      >
            <template slot="header">
              <h4 class="card-title">Synthetic Checks</h4>
              <p class="card-category">Theorectical Cost in $</p>
            </template>
            <template slot="footer">
              <div class="stats">
                <i class="fa fa-history"></i> Updated daily / page refreshed at {{last_update}}
              </div>
            </template>
          </chart-card>
        </div>
      </div>
    </div>
    	<hr/>

  </div>
</template>
<script>
  //@TODO: rewrite this whole shit
  import ChartCard from 'src/components/Cards/ChartCard.vue'
  import StatsCard from 'src/components/Cards/StatsCard.vue'
  import LTable from 'src/components/Table.vue'
  import axios from 'axios'

  import { colors, formatMoney, formatSimple, formatSimple2d, chart_base} from 'src/globalVariables'
  import { setInterval } from 'timers';
  import DxSankey, {
    DxTooltip,
    DxNode,
    DxLink,
    DxSize
  } from 'devextreme-vue/sankey';


  Number.prototype.pad = function(size) {
    var s = String(this);
    while (s.length < (size || 2)) {s = "0" + s;}
    return s;
  }

  export default {
    components: {
      LTable,
      ChartCard,
      StatsCard,
      DxSankey,
      DxTooltip,
      DxNode,
      DxLink,
      DxSize
    },
    async created() {
      this.accessToken = await this.$auth.getAccessToken()
      this.loadData()
      var vm = this
      setInterval(function(){
        vm.loadData()
      },vm.refresh_minutes*60*1000)

    },
    methods: {
      loadData: function(){
        var today = new Date();
        this.last_update = today.getHours().pad(2) + ":" + today.getMinutes().pad(2) + ":" + today.getSeconds().pad(2)
        console.log('refreshing...',)
        axios
          .get('/nr/usage?access_token=' + this.accessToken)
          .then(response => (this.sankeys =  response.data))
        axios
          .get('/nr/users_accounts?access_token=' + this.accessToken)
          .then(response => (this.users_sankey =  response.data))
        axios
          .get('/nr/users_tier?access_token=' + this.accessToken)
          .then(response => (this.tier_sankey =  response.data))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'hosts'}} )
          .then(response => (this.nr_apm_hosts =  response.data.facets))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'mobile'}} )
          .then(response => (this.nr_mobile =  response.data.timeSeries))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'infra'}} )
          .then(response => (this.nr_infra_cu =  response.data.facets))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'insights'}} )
          .then(response => (this.nr_insights =  response.data.facets))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'browser'}} )
          .then(response => (this.nr_browser_pv =  response.data.facets))
        axios
          .get('/nr/query?access_token=' + this.accessToken, {params: {account_id: this.accountId, nrql: 'synthetics'}} )
          .then(response => (this.nr_synthetic =  response.data.facets))
        axios
            .get('/nr/cost/all?access_token=' + this.accessToken)
            .then(response => (this.nr_costs =  response.data.results));

        return true
      },
      showUsers: function() {
        this.show_users = true;
        this.show_usage = false;
      },
      showUsage: function() {
        this.show_users = false;
        this.show_usage = true;
      },
    },
    watch: {
      nr_apm_hosts: function(){
        var labels = []
        var series = []

        if (this.nr_apm_hosts){
          for (let i=this.nr_apm_hosts.length-1; i>=0 ;i--) {
            labels.push(this.nr_apm_hosts[i]['name'])
            series.push({meta:formatMoney.format(this.nr_apm_hosts[i]['results'][0]['result'] * this.cost_per_host) +
                              '<br>('+formatSimple.format(this.nr_apm_hosts[i]['results'][0]['result'])+' hosts)',
                         value: this.nr_apm_hosts[i]['results'][0]['result'] * this.cost_per_host})
          }
        }
        this.nr_apm_hosts_data = {
          'data': {
            'labels': labels,
            'series': [series],
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }
        this.nr_apm_hosts_data.options.tooltip.tooltipOffset= {
                  x: 15,
                  y: 90
                },
        this.nr_apm_hosts_data.options.tooltip.tooltipFnc= function(meta, value) {
            var txt = document.createElement('textarea');
            txt.innerHTML = meta;
          return txt.value
        }
      },
      nr_mobile: function(){
        var labels = []
        var series = []
        var vm = this
        if (this.nr_mobile){
          //console.log('this.nr_mobile',this.nr_mobile)
          this.nr_mobile.forEach(function(v){
            // find the last day of each month
            var startDate = new Date(v['beginTimeSeconds']*1000)
            var endDate = new Date(v['endTimeSeconds']*1000)
            if (endDate.getDate() == 1){
              // last day of previous month
              labels.push(''+startDate.getMonth()+'-'+startDate.getFullYear())
              var kmau = v['results'][0]['sum']/ Math.pow(10,3)
              series.push({meta:formatMoney.format(kmau * vm.cost_per_1k_mau)+
              '<br>('+formatSimple.format(kmau)+'k MAU)',
              value:kmau * vm.cost_per_1k_mau})
              //console.log(v['beginTimeSeconds'],kmau)
            }
          })
        }
        this.nr_mobile_data = {
          'data': {
            'labels': labels,
            'series': [series]
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }

        // override of chart_base
        this.nr_mobile_data.options.tooltip.tooltipOffset= {
                  x: 15,
                  y: 90
                },
        this.nr_mobile_data.options.tooltip.tooltipFnc= function(meta, value) {
            var txt = document.createElement('textarea');
            txt.innerHTML = meta;
          return txt.value
        }

      },
      nr_infra_cu:function(){
        var labels = []
        var series = []
        if (this.nr_infra_cu){
          for (let i=this.nr_infra_cu.length-1; i>=0 ;i--) {
            labels.push(this.nr_infra_cu[i]['name'])
            series.push(
              {meta:formatMoney.format(this.nr_infra_cu[i]['results'][0]['sum'] * this.cost_per_1k_cu/1000) +
                              '<br>('+formatSimple.format(Math.floor(this.nr_infra_cu[i]['results'][0]['sum']/1000))+'K C.U.)',
                         value: this.nr_infra_cu[i]['results'][0]['sum'] * this.cost_per_1k_cu/1000})
          }
        }
        this.nr_infra_cu_data = {
          'data': {
            'labels': labels,
            'series': [series]
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }

        // override of chart_base
        this.nr_infra_cu_data.options.tooltip.tooltipFnc= function(meta, value) {
            var txt = document.createElement('textarea');
            txt.innerHTML = meta;
          return txt.value
        }

      },
      nr_insights: function(){
        var labels = []
        var series = []
        if (this.nr_insights){
          for (let i=this.nr_insights.length-1; i>=0 ;i--) {
            labels.push(this.nr_insights[i]['name'])
            series.push(
              {meta:formatMoney.format(this.nr_insights[i]['results'][0]['result'] * this.cost_per_1M_events /1000000) +
                              '<br>('+formatSimple.format(Math.floor(this.nr_insights[i]['results'][0]['result']/1000000))+'M Events)',
                         value: this.nr_insights[i]['results'][0]['result'] * this.cost_per_1M_events/1000000})
              }
        }
        this.nr_insights_data = {
          'data': {
            'labels': labels,
            'series': [series]
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }
        this.nr_insights_data.options.tooltip.tooltipOffset= {
                  x: 15,
                  y: 90
        }
      },
      nr_browser_pv: function(){
        var labels = []
        var series = []
        if (this.nr_browser_pv){
          for (let i=this.nr_browser_pv.length-1; i>=0 ;i--) {
            labels.push(this.nr_browser_pv[i]['name'])
            series.push(
              {meta:formatMoney.format(this.nr_browser_pv[i]['results'][0]['sum'] * this.cost_per_1k_pv /1000) +
                              '<br>('+formatSimple2d.format(this.nr_browser_pv[i]['results'][0]['sum']/1000000)+'M P.V.)',
                         value: this.nr_browser_pv[i]['results'][0]['sum'] * this.cost_per_1k_pv/1000})
          }
        }
        this.nr_browser_pv_data = {
          'data': {
            'labels': labels,
            'series': [series]
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }

        //override of chart_base
        this.nr_browser_pv_data.options.tooltip.transformTooltipTextFnc = function(value) {
                return formatSimple.format(value)
        }

      },
      nr_synthetic: function(){
        var labels = []
        var series = []
        if (this.nr_synthetic){
          for (let i=this.nr_synthetic.length-1; i>=0 ;i--) {
            labels.push(this.nr_synthetic[i]['name'])
            series.push(
                  {meta:formatMoney.format(this.nr_synthetic[i]['results'][0]['sum'] * this.cost_per_1k_checks /1000) +
                    '<br>('+formatSimple2d.format(this.nr_synthetic[i]['results'][0]['sum']/1000000)+'M P.V.)',
                  value: this.nr_synthetic[i]['results'][0]['sum'] * this.cost_per_1k_checks/1000})
          }
        }
        this.nr_synthetic_data = {
          'data': {
            'labels': labels,
            'series': [series]
          },
          'options': Object.assign({}, chart_base.options),
          'responsiveOptions': Object.assign({}, chart_base.responsiveOptions),
        }

        //override of chart_base
        this.nr_synthetic_data.options.tooltip.transformTooltipTextFnc = function(value) {
                return value
        }
      },
	  nr_costs: function (){
		let services = [];
		for (let service in this.nr_costs) {
			services.push(service)
			let [labels, series] = [[], [], []];
			for (let account in this.nr_costs[service]) {
				labels.push(Object.keys(this.nr_costs[service][account])[0]);
				series.push(Object.values(this.nr_costs[service][account])[0]);
			}
			this.nr_costs_data[service] = {
				'data': {
					'labels': labels,
					'series': [series]
				},
				'options': {
					'height': '700px',
					'horizontalBars': true,
					'seriesBarWidth': 10,
					'seriesBarDistance': 10,
					'axisX': {
						'showGrid': true
					},
					'chartPadding': {
						'right': 0,
						'left': 170,
					},
					'tooltip':{
						'tooltipFnc': function(meta, value){
							return(value)
						},

						tooltipOffset: {
							x: 15,
							y: 90
						},
						'appendToBody':false,
						'transformTooltipTextFnc': function(value) {
							return ''+ formatMoney.format(value)
						}
					},
				},
				'responsiveOptions': [
					['screen and (max-width: 640px)', {
						'seriesBarDistance': 6,
						'axisX': {
							labelInterpolationFnc (value) {
								return value[0]
							}
						}
					}]
				]
			}
		}
		this.loading = false;
	  },
	},
    computed: {

    },
    props:{
      accountId:{
        type:String,
        default: function () {
          return '1702279'
        }
      }
    },
    data () {
      return {
        accessToken:'',
        cost_per_host:50,         //apm
        cost_per_1k_pv:0.0167,    // browser
        cost_per_1k_mau:0.439,    // mobile
        cost_per_1k_cu:0.8333,    // infra
        cost_per_1k_checks:4.72,  // synthetic
        cost_per_1M_events:1.265, // insights
        refresh_minutes: 30,
        last_update: '',
        colors: colors,
        nr_apm_hosts:[],
        chart_base: chart_base,
        nr_apm_hosts_data:{},
        nr_apm_cu:[],
        nr_apm_cu_data:{},
        nr_infra_cu:[],
        nr_infra_cu_data:{},
        nr_insights:[],
        nr_insights_data:{},
        nr_browser_pv:[],
        nr_browser_pv_data:{},
        nr_synthetic:[],
        nr_synthetic_data:{},
        nr_mobile:[],
        nr_mobile_data:{},
        nr_costs:{},
        nr_costs_data: {},
        sankeys:[],
        users_sankey:[],
        tier_sankey: [],
        show_users: false,
        show_usage: true,
        loading: true
      }
    }
  }
</script>
<style>
	.cost .card .ct-chart {
		height: 700px;
	}
/* .my-row {
  border: 2px solid green;
}
.my-col {
  border: 2px solid green;
} */
</style>
